import { zodResolver } from '@hookform/resolvers/zod';
import { ApiClientError } from '@recipes/api-client';
import { registerRequestSchema, type RegisterRequest } from '@recipes/contracts';
import { defaultMeasurementSystem } from '@recipes/domain';
import type { z } from 'zod';
import { Alert, Button, Checkbox, Input, Label } from '@recipes/ui';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { FormField } from '@/components/forms/form-field';
import { AuthModeLoading, IdentitySignInButton, useAuthMode } from '@/features/auth/identity';
import { PasswordField } from '@/features/profile/password-field';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { AuthLayout } from './auth-layout';

export function RegisterPage() {
  const { t } = useTranslation();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [error, setError] = useState<string | null>(null);
  const [verificationRequired, setVerificationRequired] = useState(false);

  const form = useForm<z.input<typeof registerRequestSchema>, unknown, RegisterRequest>({
    resolver: zodResolver(registerRequestSchema),
    defaultValues: {
      email: '',
      displayName: '',
      password: '',
      locale: currentLocale(),
      acceptTerms: undefined as unknown as true,
    },
  });
  const password = form.watch('password');

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await registerUser({
        ...values,
        locale: currentLocale(),
        measurementSystem: defaultMeasurementSystem(navigator.languages ?? [navigator.language]),
      });
      if (result.verificationRequired) setVerificationRequired(true);
      else navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'CONFLICT')
        setError(t('auth.errors.emailTaken'));
      else setError(describeError(err));
    }
  });

  const authMode = useAuthMode();
  if (authMode.mode === undefined) return <AuthModeLoading />;
  if (authMode.mode === 'oidc' && authMode.identity) {
    return (
      <AuthLayout
        title={t('auth.register')}
        description={t('auth.identity.registerDescription')}
        testId="register-page"
      >
        <IdentitySignInButton
          identity={authMode.identity}
          returnTo={from}
          label={t('auth.identity.register')}
          testId="identity-register"
        />
      </AuthLayout>
    );
  }

  if (verificationRequired) {
    return (
      <AuthLayout title={t('auth.checkInbox')} testId="register-verification">
        <Alert variant="success">{t('auth.verificationSent')}</Alert>
        <Button asChild className="mt-4 w-full" variant="outline">
          <Link to="/login">{t('auth.backToLogin')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.register')}
      testId="register-page"
      footer={
        <>
          {t('auth.hasAccount')}{' '}
          <Link
            to="/login"
            state={{ from }}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('auth.login')}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {error && (
          <Alert variant="destructive" data-testid="register-error">
            {error}
          </Alert>
        )}
        <FormField
          label={t('auth.email')}
          error={form.formState.errors.email && t('auth.errors.email')}
          required
        >
          <Input
            type="email"
            autoComplete="email"
            inputMode="email"
            data-testid="register-email"
            {...form.register('email')}
          />
        </FormField>
        <FormField
          label={t('auth.displayName')}
          error={form.formState.errors.displayName && t('auth.errors.displayName')}
          required
        >
          <Input
            autoComplete="nickname"
            maxLength={80}
            data-testid="register-display-name"
            {...form.register('displayName')}
          />
        </FormField>
        <FormField
          label={t('auth.password')}
          error={form.formState.errors.password && t('auth.errors.passwordWeak')}
          required
        >
          <PasswordField
            autoComplete="new-password"
            value={password}
            showStrength
            data-testid="register-password"
            {...form.register('password')}
          />
        </FormField>
        <Controller
          control={form.control}
          name="acceptTerms"
          render={({ field, fieldState }) => (
            <div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="accept-terms"
                  checked={field.value === true}
                  onCheckedChange={(v) => field.onChange(v === true ? true : undefined)}
                  aria-invalid={fieldState.error ? true : undefined}
                  aria-describedby={fieldState.error ? 'accept-terms-error' : undefined}
                  data-testid="register-terms"
                  className="mt-0.5"
                />
                <Label htmlFor="accept-terms" className="leading-snug">
                  {t('auth.acceptTerms')}
                </Label>
              </div>
              {fieldState.error && (
                <p id="accept-terms-error" role="alert" className="mt-1 text-sm text-destructive">
                  {t('auth.errors.terms')}
                </p>
              )}
            </div>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          loading={form.formState.isSubmitting}
          data-testid="register-submit"
        >
          {t('auth.register')}
        </Button>
      </form>
    </AuthLayout>
  );
}
